class EasyBundleV2Component extends HTMLElement {
  constructor() {
    super();

    this.state = {
      selectedBottles: [],
      bundleId: this.generateBundleId(),
      currentTab: 1,
    };

    // Valid tier quantities for checkout (removed 6)
    this.tiers = [4, 8, 12];
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get combination-based discount codes from data attributes
    // Format: discount_XstdYsm where X = standard count, Y = max count
    this.discountCodes = {
      // Tier 4 combinations
      '4std_0sm': this.dataset.discount4std0sm || '4STD',
      '3std_1sm': this.dataset.discount3std1sm || '3STD1SM',
      '2std_2sm': this.dataset.discount2std2sm || '2STD2SM',
      '1std_3sm': this.dataset.discount1std3sm || '1STD3SM',
      '0std_4sm': this.dataset.discount0std4sm || '4SM',
      // Tier 8 combinations
      '8std_0sm': this.dataset.discount8std0sm || '8STD',
      '7std_1sm': this.dataset.discount7std1sm || '7STD1SM',
      '6std_2sm': this.dataset.discount6std2sm || '6STD2SM',
      '5std_3sm': this.dataset.discount5std3sm || '5STD3SM',
      '4std_4sm': this.dataset.discount4std4sm || '4STD4SM',
      '3std_5sm': this.dataset.discount3std5sm || '3STD5SM',
      '2std_6sm': this.dataset.discount2std6sm || '2STD6SM',
      '1std_7sm': this.dataset.discount1std7sm || '1STD7SM',
      '0std_8sm': this.dataset.discount0std8sm || '8SM',
      // Tier 12 combinations
      '12std_0sm': this.dataset.discount12std0sm || '12STD',
      '11std_1sm': this.dataset.discount11std1sm || '11STD1SM',
      '10std_2sm': this.dataset.discount10std2sm || '10STD2SM',
      '9std_3sm': this.dataset.discount9std3sm || '9STD3SM',
      '8std_4sm': this.dataset.discount8std4sm || '8STD4SM',
      '7std_5sm': this.dataset.discount7std5sm || '7STD5SM',
      '6std_6sm': this.dataset.discount6std6sm || '6STD6SM',
      '5std_7sm': this.dataset.discount5std7sm || '5STD7SM',
      '4std_8sm': this.dataset.discount4std8sm || '4STD8SM',
      '3std_9sm': this.dataset.discount3std9sm || '3STD9SM',
      '2std_10sm': this.dataset.discount2std10sm || '2STD10SM',
      '1std_11sm': this.dataset.discount1std11sm || '1STD11SM',
      '0std_12sm': this.dataset.discount0std12sm || '12SM',
    };

    // Get combination-based discount percentages from data attributes (for display)
    this.discountPercents = {
      // Tier 4 combinations (20% off)
      '4std_0sm': this.dataset.percent4std0sm || '20%',
      '3std_1sm': this.dataset.percent3std1sm || '20%',
      '2std_2sm': this.dataset.percent2std2sm || '20%',
      '1std_3sm': this.dataset.percent1std3sm || '20%',
      '0std_4sm': this.dataset.percent0std4sm || '20%',
      // Tier 8 combinations (25% off)
      '8std_0sm': this.dataset.percent8std0sm || '25%',
      '7std_1sm': this.dataset.percent7std1sm || '25%',
      '6std_2sm': this.dataset.percent6std2sm || '25%',
      '5std_3sm': this.dataset.percent5std3sm || '25%',
      '4std_4sm': this.dataset.percent4std4sm || '25%',
      '3std_5sm': this.dataset.percent3std5sm || '25%',
      '2std_6sm': this.dataset.percent2std6sm || '25%',
      '1std_7sm': this.dataset.percent1std7sm || '25%',
      '0std_8sm': this.dataset.percent0std8sm || '25%',
      // Tier 12 combinations (30% off)
      '12std_0sm': this.dataset.percent12std0sm || '30%',
      '11std_1sm': this.dataset.percent11std1sm || '30%',
      '10std_2sm': this.dataset.percent10std2sm || '30%',
      '9std_3sm': this.dataset.percent9std3sm || '30%',
      '8std_4sm': this.dataset.percent8std4sm || '30%',
      '7std_5sm': this.dataset.percent7std5sm || '30%',
      '6std_6sm': this.dataset.percent6std6sm || '30%',
      '5std_7sm': this.dataset.percent5std7sm || '30%',
      '4std_8sm': this.dataset.percent4std8sm || '30%',
      '3std_9sm': this.dataset.percent3std9sm || '30%',
      '2std_10sm': this.dataset.percent2std10sm || '30%',
      '1std_11sm': this.dataset.percent1std11sm || '30%',
      '0std_12sm': this.dataset.percent0std12sm || '30%',
    };

    // Setup tab navigation
    this.setupTabs();

    // Setup product buttons
    this.setupProductButtons();

    // Setup checkout button
    this.setupCheckoutButton();

    // Setup sticky footer
    this.setupStickyFooter();

    // Initialize UI
    this.updateUI();
    this.updateStickyFooter();
  }

  // ==================== TIER HELPER METHODS ====================

  getBottleCount() {
    return this.state.selectedBottles.reduce((sum, bottle) => sum + bottle.quantity, 0);
  }

  // Count standard products only
  getStandardCount() {
    return this.state.selectedBottles
      .filter(b => b.type === 'standard')
      .reduce((sum, b) => sum + b.quantity, 0);
  }

  // Count max strength products only
  getMaxCount() {
    return this.state.selectedBottles
      .filter(b => b.type === 'max')
      .reduce((sum, b) => sum + b.quantity, 0);
  }

  // Get the combination key for discount lookup
  getCombinationKey() {
    const stdCount = this.getStandardCount();
    const maxCount = this.getMaxCount();
    return `${stdCount}std_${maxCount}sm`;
  }

  getCurrentTier(count) {
    if (count >= 12) return 12;
    if (count >= 8) return 8;
    if (count >= 4) return 4;
    return 0;
  }

  getNextTier(count) {
    for (const tier of this.tiers) {
      if (count < tier) return tier;
    }
    return null; // Already at or above max tier
  }

  canCheckout(count) {
    return this.tiers.includes(count);
  }

  // Get discount code based on STD/SM combination
  getDiscountCode() {
    const total = this.getBottleCount();
    if (!this.tiers.includes(total)) return null;

    const key = this.getCombinationKey();
    return this.discountCodes[key] || null;
  }

  // Get discount percentage based on STD/SM combination
  getDiscountPercent() {
    const total = this.getBottleCount();
    if (!this.tiers.includes(total)) return '';

    const key = this.getCombinationKey();
    return this.discountPercents[key] || '';
  }

  // Get next tier's discount percentage (for display purposes)
  getNextTierPercent() {
    const count = this.getBottleCount();
    const nextTier = this.getNextTier(count);
    if (!nextTier) return '';

    // Show a default percentage for the next tier
    if (nextTier === 4) return '20%';
    if (nextTier === 8) return '25%';
    if (nextTier === 12) return '30%';
    return '';
  }

  // ==================== SETUP METHODS ====================

  setupTabs() {
    const tabs = this.querySelectorAll('[data-tab]');
    const panels = this.querySelectorAll('[data-tab-panel]');
    const tabsWrapper = this.querySelector('.bundle-builder__tabs-wrapper');

    // Ensure tabs start scrolled to the beginning on mobile
    if (tabsWrapper) {
      // Reset scroll position immediately and after a short delay (for page load)
      tabsWrapper.scrollLeft = 0;
      setTimeout(() => {
        tabsWrapper.scrollLeft = 0;
      }, 50);
      setTimeout(() => {
        tabsWrapper.scrollLeft = 0;
      }, 150);
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const tabNumber = parseInt(tab.dataset.tab);

        // Update active states
        tabs.forEach((t) => t.classList.remove('bundle-builder__main-tab--active'));
        panels.forEach((p) => p.classList.remove('bundle-builder__main-panel--active'));

        tab.classList.add('bundle-builder__main-tab--active');
        const activePanel = this.querySelector(`[data-tab-panel="${tabNumber}"]`);
        if (activePanel) {
          activePanel.classList.add('bundle-builder__main-panel--active');
        }

        // Scroll the clicked tab into view (for mobile)
        if (tabsWrapper && window.innerWidth <= 750) {
          setTimeout(() => {
            const tabRect = tab.getBoundingClientRect();
            const wrapperRect = tabsWrapper.getBoundingClientRect();
            const scrollLeft = tab.offsetLeft - (wrapperRect.width / 2) + (tabRect.width / 2);
            tabsWrapper.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
          }, 50);
        }

        this.state.currentTab = tabNumber;
      });
    });
  }

  setupProductButtons() {
    const addButtons = this.querySelectorAll('[data-add-to-bundle]');
    const quantityButtons = this.querySelectorAll('[data-action]');

    addButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.handleAddToBundle(e));
    });

    quantityButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const action = button.dataset.action;
        if (action === 'increase') {
          this.increaseQuantity(e);
        } else if (action === 'decrease') {
          this.decreaseQuantity(e);
        }
      });
    });
  }

  setupCheckoutButton() {
    const checkoutBtn = this.querySelector('[data-checkout-btn]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => this.goToCheckout());
    }
  }

  setupStickyFooter() {
    const footer = this.querySelector('[data-sticky-footer]');
    const footerBar = this.querySelector('[data-footer-bar]');
    const footerClose = this.querySelector('[data-footer-close]');
    const footerCheckout = this.querySelector('[data-footer-checkout]');
    const footerCheckoutOutside = this.querySelector('[data-footer-checkout-btn-outside]');

    if (footerBar) {
      footerBar.addEventListener('click', () => {
        footer.classList.add('bundle-builder__sticky-footer--expanded');
      });
    }

    if (footerClose) {
      footerClose.addEventListener('click', () => {
        footer.classList.remove('bundle-builder__sticky-footer--expanded');
      });
    }

    if (footerCheckout) {
      footerCheckout.addEventListener('click', () => this.goToCheckout());
    }

    if (footerCheckoutOutside) {
      footerCheckoutOutside.addEventListener('click', () => this.goToCheckout());
    }
  }

  // ==================== PRODUCT HANDLING ====================

  async handleAddToBundle(event) {
    const button = event.target.closest('[data-add-to-bundle]');
    if (!button || button.disabled) return;

    const productCard = button.closest('.bundle-builder__product-card');
    const productId = productCard.dataset.productId;
    const variantId = productCard.dataset.variantId;
    const title = productCard.dataset.productTitle;
    const price = parseInt(productCard.dataset.productPrice);
    const originalPrice = parseInt(productCard.dataset.productOriginalPrice);
    const image = productCard.dataset.productImage;
    const type = productCard.dataset.productType || 'standard';

    const btnText = button.querySelector('.bundle-builder__add-btn-text');
    const qtyInput = productCard.querySelector('.bundle-builder__qty-input');

    // Get quantity from input (default to 1 if not found)
    const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

    // Show "Adding to bundle..." state
    button.disabled = true;
    button.classList.add('bundle-builder__add-btn--loading');
    if (btnText) btnText.textContent = 'Adding...';

    try {
      // Add the specified quantity to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: {
            _bundle_id: this.state.bundleId,
            _bundle_type: 'combo-bundle',
            _product_type: type,
            _source: 'easy_bundle_v2',
          }
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.description || data.message || `Failed to add ${title}`;
        throw new Error(errorMsg);
      }

      // Check if product already in state
      let bottle = this.state.selectedBottles.find(b => b.productId === productId);

      if (bottle) {
        // Update existing quantity
        bottle.quantity += quantity;
      } else {
        // Add new product to state
        this.state.selectedBottles.push({
          productId,
          variantId,
          title,
          price,
          originalPrice,
          image,
          type,
          quantity: quantity,
        });
      }

      // Show "Added" state briefly, then reset
      button.classList.remove('bundle-builder__add-btn--loading');
      button.classList.add('bundle-builder__add-btn--added');
      if (btnText) btnText.textContent = 'Added!';

      // After 800ms, reset button to normal state
      setTimeout(() => {
        button.classList.remove('bundle-builder__add-btn--added');
        button.disabled = false;
        if (btnText) btnText.textContent = 'Add to Bundle';
      }, 800);

      this.showInlineMessage(productId, `${quantity}x added to bundle`, 'success');
      this.updateUI();
      this.updateStickyFooter();

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.classList.remove('bundle-builder__add-btn--loading');
      button.disabled = false;
      if (btnText) btnText.textContent = 'Add to Bundle';
      this.showInlineMessage(productId, 'Failed to add', 'removed');
    }
  }

  increaseQuantity(event) {
    const button = event.target.closest('[data-action="increase"]');
    if (!button || button.disabled) return;

    const productCard = button.closest('.bundle-builder__product-card');
    const qtyInput = productCard.querySelector('.bundle-builder__qty-input');

    if (qtyInput) {
      const currentQty = parseInt(qtyInput.value) || 1;
      const newQty = currentQty + 1;
      qtyInput.value = newQty;
    }
  }

  decreaseQuantity(event) {
    const button = event.target.closest('[data-action="decrease"]');
    if (!button || button.disabled) return;

    const productCard = button.closest('.bundle-builder__product-card');
    const qtyInput = productCard.querySelector('.bundle-builder__qty-input');

    if (qtyInput) {
      const currentQty = parseInt(qtyInput.value) || 1;
      if (currentQty > 1) {
        const newQty = currentQty - 1;
        qtyInput.value = newQty;
      }
    }
  }

  async removeFromBundle(productId) {
    const bottle = this.state.selectedBottles.find(b => b.productId === productId);
    if (!bottle || bottle.quantity === 0) return;

    const variantId = bottle.variantId;

    try {
      // Update cart - decrease by 1
      await this.updateCartQuantity(variantId, -1);

      // Update local state
      bottle.quantity -= 1;

      if (bottle.quantity === 0) {
        // Remove from array if quantity is 0
        const index = this.state.selectedBottles.findIndex(b => b.productId === productId);
        this.state.selectedBottles.splice(index, 1);
      }

      this.showInlineMessage(productId, 'Removed from bundle', 'removed');
      this.updateUI();
      this.updateStickyFooter();

    } catch (error) {
      console.error('Error removing from cart:', error);
      this.showInlineMessage(productId, 'Failed to remove', 'removed');
    }
  }

  async updateCartQuantity(variantId, delta) {
    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find all cart items with this variant and bundle ID
      const cartItems = cart.items.filter(item =>
        item.variant_id == variantId &&
        item.properties._bundle_id === this.state.bundleId
      );

      if (cartItems.length > 0) {
        // Get total quantity for this product in cart
        const totalQty = cartItems.reduce((sum, item) => sum + item.quantity, 0);
        const newQty = totalQty + delta;

        // Update the first item to the new quantity
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: cartItems[0].key,
            quantity: Math.max(0, newQty)
          })
        });

        // Remove any duplicate items
        for (let i = 1; i < cartItems.length; i++) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: cartItems[i].key,
              quantity: 0
            })
          });
        }
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error);
      throw error;
    }
  }

  // ==================== UI UPDATE METHODS ====================

  updateUI() {
    this.updateTierIndicators();
    this.updateProgressMessage();
    this.updateCheckoutButton();
    this.updateCombinationDisplay();
  }

  updateTierIndicators() {
    const count = this.getBottleCount();
    const currentTier = this.getCurrentTier(count);
    const nextTier = this.getNextTier(count);
    const indicators = this.querySelectorAll('.bundle-builder__tier-indicator');
    const connectors = this.querySelectorAll('.bundle-builder__tier-connector');

    indicators.forEach(indicator => {
      const tierValue = parseInt(indicator.dataset.tier);

      // Remove all state classes
      indicator.classList.remove(
        'bundle-builder__tier-indicator--active',
        'bundle-builder__tier-indicator--completed',
        'bundle-builder__tier-indicator--next'
      );

      if (count >= tierValue) {
        // This tier is completed or active
        if (tierValue === currentTier && this.canCheckout(count)) {
          indicator.classList.add('bundle-builder__tier-indicator--active');
        } else if (count > tierValue) {
          indicator.classList.add('bundle-builder__tier-indicator--completed');
        } else if (count === tierValue) {
          indicator.classList.add('bundle-builder__tier-indicator--active');
        }
      } else if (tierValue === nextTier) {
        // This is the next tier to reach
        indicator.classList.add('bundle-builder__tier-indicator--next');
      }
    });

    // Update connectors
    let connectorIndex = 0;
    connectors.forEach(connector => {
      connector.classList.remove('bundle-builder__tier-connector--active');

      // Activate connector if we've passed this point
      const tierBefore = this.tiers[connectorIndex];
      if (count >= tierBefore) {
        connector.classList.add('bundle-builder__tier-connector--active');
      }
      connectorIndex++;
    });
  }

  updateCombinationDisplay() {
    const comboEl = this.querySelector('[data-combo-display]');
    if (!comboEl) return;

    const stdCount = this.getStandardCount();
    const maxCount = this.getMaxCount();
    const total = this.getBottleCount();

    if (total === 0) {
      comboEl.textContent = '';
    } else {
      comboEl.textContent = `${stdCount} Standard + ${maxCount} Max Strength`;
    }
  }

  updateProgressMessage() {
    const messageEl = this.querySelector('[data-progress-message]');
    if (!messageEl) return;

    const count = this.getBottleCount();
    const currentTier = this.getCurrentTier(count);
    const nextTier = this.getNextTier(count);
    const canCheckout = this.canCheckout(count);
    const stdCount = this.getStandardCount();
    const maxCount = this.getMaxCount();

    let message = '';

    if (count === 0) {
      message = 'Start building your bundle (min 4 products)';
      messageEl.classList.remove('bundle-builder__progress-message--ready');
    } else if (canCheckout) {
      // At a valid tier - show current discount
      const currentPercent = this.getDiscountPercent();
      if (nextTier) {
        const remaining = nextTier - count;
        const nextPercent = this.getNextTierPercent();
        message = `${count}-Pack Ready! ${currentPercent} off | Add ${remaining} more for ${nextTier}-Pack (${nextPercent} off)`;
      } else {
        message = `${count}-Pack Ready! ${currentPercent} off | Maximum bundle reached!`;
      }
      messageEl.classList.add('bundle-builder__progress-message--ready');
    } else if (nextTier) {
      // Between tiers - show what they're working towards
      const remaining = nextTier - count;
      const nextPercent = this.getNextTierPercent();
      message = `${count} products selected | Add ${remaining} more for ${nextTier}-Pack (${nextPercent} off)`;
      messageEl.classList.remove('bundle-builder__progress-message--ready');
    }

    messageEl.textContent = message;
  }

  updateCheckoutButton() {
    const checkoutBtn = this.querySelector('[data-checkout-btn]');
    const footerCheckoutBtn = this.querySelector('[data-footer-checkout]');
    const outsideCheckoutBtn = this.querySelector('[data-footer-checkout-btn-outside]');

    const count = this.getBottleCount();
    const canCheckout = this.canCheckout(count);
    const nextTier = this.getNextTier(count);

    // Prepare button text
    let buttonText = '';
    let buttonDisabled = true;

    if (canCheckout) {
      buttonText = `Checkout ${count}-Pack`;
      buttonDisabled = false;
    } else if (count > 0 && nextTier) {
      const remaining = nextTier - count;
      buttonText = `Add ${remaining} more for ${nextTier}-Pack`;
      buttonDisabled = true;
    } else {
      buttonText = 'Add products to bundle';
      buttonDisabled = true;
    }

    // Update all checkout buttons
    [checkoutBtn, footerCheckoutBtn, outsideCheckoutBtn].forEach(btn => {
      if (btn) {
        btn.disabled = buttonDisabled;
        const spanEl = btn.querySelector('span');
        if (spanEl) {
          spanEl.textContent = buttonText;
        } else {
          btn.textContent = buttonText;
        }

        if (canCheckout) {
          btn.classList.add('bundle-builder__checkout-btn--ready');
        } else {
          btn.classList.remove('bundle-builder__checkout-btn--ready');
        }
      }
    });
  }

  updateStickyFooter() {
    const footer = this.querySelector('[data-sticky-footer]');
    const footerItems = this.querySelector('[data-footer-items]');
    const footerCount = this.querySelector('[data-footer-count]');
    const footerBarText = this.querySelector('[data-footer-bar-text]');
    const footerProgressText = this.querySelector('[data-footer-progress-text]');
    const footerTitle = this.querySelector('[data-footer-title]');

    if (!footer || !footerItems) return;

    const count = this.getBottleCount();
    const currentTier = this.getCurrentTier(count);
    const nextTier = this.getNextTier(count);
    const canCheckout = this.canCheckout(count);
    const stdCount = this.getStandardCount();
    const maxCount = this.getMaxCount();

    // Always show footer
    footer.classList.add('bundle-builder__sticky-footer--visible');

    // Update count
    if (footerCount) {
      footerCount.textContent = `(${count})`;
    }

    // Update footer title
    if (footerTitle) {
      if (currentTier > 0) {
        footerTitle.textContent = `${currentTier}-Pack Bundle`;
      } else {
        footerTitle.textContent = 'Your Bundle';
      }
    }

    // Generate progress text with discount percentages
    let progressText = '';
    if (count === 0) {
      progressText = 'Start building your bundle (min 4 products)';
    } else if (canCheckout) {
      const currentPercent = this.getDiscountPercent();
      if (nextTier) {
        const remaining = nextTier - count;
        const nextPercent = this.getNextTierPercent();
        progressText = `${count}-Pack ${currentPercent} off! +${remaining} for ${nextTier}-Pack`;
      } else {
        progressText = `${count}-Pack ${currentPercent} off!`;
      }
    } else if (nextTier) {
      const remaining = nextTier - count;
      const nextPercent = this.getNextTierPercent();
      progressText = `${count} products | +${remaining} for ${nextTier}-Pack (${nextPercent} off)`;
    }

    // Update mobile footer bar text
    if (footerBarText) {
      footerBarText.textContent = progressText;
    }

    // Update desktop footer progress text
    if (footerProgressText) {
      footerProgressText.textContent = progressText;
    }

    // Update footer items
    footerItems.innerHTML = '';

    // Add bottle items with quantity badges
    this.state.selectedBottles.forEach(bottle => {
      const item = document.createElement('div');
      item.className = 'bundle-builder__footer-item';
      item.innerHTML = `
        <img src="${bottle.image}" alt="${bottle.title}">
        ${bottle.quantity > 1 ? `<span class="bundle-builder__footer-item-qty">${bottle.quantity}x</span>` : ''}
        <span class="bundle-builder__footer-item-type">${bottle.type === 'max' ? 'SM' : 'STD'}</span>
        <button class="bundle-builder__footer-item-remove" data-remove-product="${bottle.productId}">×</button>
      `;

      const removeBtn = item.querySelector('[data-remove-product]');
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeFromBundle(bottle.productId);
      });

      footerItems.appendChild(item);
    });

    // Show outside checkout button
    const outsideCheckout = this.querySelector('[data-footer-checkout-outside]');
    if (outsideCheckout) {
      outsideCheckout.style.display = 'block';
    }
  }

  // ==================== CHECKOUT ====================

  async goToCheckout() {
    const count = this.getBottleCount();

    if (!this.canCheckout(count)) {
      const nextTier = this.getNextTier(count);
      if (nextTier) {
        alert(`Please add ${nextTier - count} more products to complete your ${nextTier}-Pack bundle.`);
      } else {
        alert('Please add at least 4 products to your bundle.');
      }
      return;
    }

    try {
      console.log('[Easy Bundle V2] Starting checkout process...');
      console.log(`[Easy Bundle V2] Combination: ${this.getCombinationKey()}`);

      // Show loading state
      const checkoutBtns = this.querySelectorAll('[data-footer-checkout], [data-footer-checkout-btn-outside], [data-checkout-btn]');
      checkoutBtns.forEach(btn => {
        if (btn) {
          btn.disabled = true;
          btn.style.opacity = '0.5';
          const spanEl = btn.querySelector('span');
          if (spanEl) {
            spanEl.textContent = 'Preparing...';
          } else {
            btn.textContent = 'Preparing...';
          }
        }
      });

      // Create loading overlay
      const loadingOverlay = document.createElement('div');
      loadingOverlay.className = 'easy-bundle-loading-overlay';
      loadingOverlay.innerHTML = `
        <div class="loading-spinner"></div>
        <div class="loading-text">Preparing your ${count}-Pack bundle...</div>
      `;
      loadingOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 99999;
        color: white;
        font-size: 18px;
      `;
      document.body.appendChild(loadingOverlay);

      // Add all bottles from state to cart
      console.log('[Easy Bundle V2] Adding all bottles to cart...');
      for (const bottle of this.state.selectedBottles) {
        console.log(`[Easy Bundle V2] Adding to cart: ${bottle.title} (qty: ${bottle.quantity}, type: ${bottle.type})`);

        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: bottle.variantId,
                quantity: bottle.quantity
              }]
            })
          });
        } catch (error) {
          console.error('[Easy Bundle V2] Error adding bottle:', error);
        }
      }

      // Get discount code for current combination
      const discountCode = this.getDiscountCode();

      // Build checkout URL
      let checkoutUrl = '/checkout';
      if (discountCode && discountCode.trim() !== '') {
        checkoutUrl = `/checkout?discount=${encodeURIComponent(discountCode.trim())}`;
      }

      console.log('[Easy Bundle V2] Redirecting to checkout:', checkoutUrl);
      console.log('[Easy Bundle V2] Discount code:', discountCode);

      // Redirect to checkout
      // Checkout must go through the sticky.io redirect, never native Shopify checkout
      if (window.mcStickyRedirect) { window.mcStickyRedirect(); return; }
      window.location.href = checkoutUrl;

    } catch (error) {
      console.error('[Easy Bundle V2] Checkout error:', error);

      // Remove loading overlay
      const overlay = document.querySelector('.easy-bundle-loading-overlay');
      if (overlay) {
        overlay.remove();
      }

      // Re-enable buttons
      const checkoutBtns = this.querySelectorAll('[data-footer-checkout], [data-footer-checkout-btn-outside], [data-checkout-btn]');
      checkoutBtns.forEach(btn => {
        if (btn) {
          btn.disabled = false;
          btn.style.opacity = '1';
        }
      });

      this.updateCheckoutButton();
      alert('There was an error preparing your checkout. Please try again.');
    }
  }

  // ==================== UTILITIES ====================

  showInlineMessage(productId, message, type = 'success') {
    const messageEl = this.querySelector(`[data-add-message="${productId}"]`);
    if (!messageEl) return;

    messageEl.textContent = message;
    messageEl.classList.add('bundle-builder__add-message--show');

    if (type === 'success') {
      messageEl.classList.add('bundle-builder__add-message--success');
      messageEl.classList.remove('bundle-builder__add-message--removed');
    } else if (type === 'removed') {
      messageEl.classList.add('bundle-builder__add-message--removed');
      messageEl.classList.remove('bundle-builder__add-message--success');
    }

    setTimeout(() => {
      messageEl.classList.remove('bundle-builder__add-message--show');

      setTimeout(() => {
        messageEl.classList.remove('bundle-builder__add-message--success', 'bundle-builder__add-message--removed');
        messageEl.textContent = '';
      }, 300);
    }, 2500);
  }

  generateBundleId() {
    return `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Register the custom element
customElements.define('easy-bundle-v2-component', EasyBundleV2Component);
